import {SafeHTML, Type} from '@github-ui/react-query-persister/typebox'
import type {SafeHtml} from '@github-ui/dotcom-schema/types/common/v1/safe_html'
import type {Icon} from '@primer/octicons-react'
import type {LabelProps} from '@primer/react'
import type {IssueItem, IssueDeferredItem} from '../../issues/types/list-types'

export const DisplayStateType = {
  Draft: 'DRAFT',
  Opened: 'OPEN',
  Closed: 'CLOSED',
  Merged: 'MERGED',
  Queued: 'QUEUED',
} as const

export const DisplayStateSchema = Type.Union([
  Type.Literal(DisplayStateType.Draft),
  Type.Literal(DisplayStateType.Opened),
  Type.Literal(DisplayStateType.Closed),
  Type.Literal(DisplayStateType.Merged),
  Type.Literal(DisplayStateType.Queued),
])

export type DisplayStateType = Type.Static<typeof DisplayStateSchema>

export const StateType = {
  Opened: 'OPEN',
  Closed: 'CLOSED',
  Merged: 'MERGED',
} as const

const StateSchema = Type.Union([
  Type.Literal(StateType.Opened),
  Type.Literal(StateType.Closed),
  Type.Literal(StateType.Merged),
])

export type StateType = Type.Static<typeof StateSchema>

export const DeferredCategory = {
  MergeConflicts: 'MERGE_CONFLICTS',
  OutdatedBranch: 'OUTDATED_BRANCH',
  ChangesRequested: 'CHANGES_REQUESTED',
  CiFailing: 'CI_FAILING',
  CiRunning: 'CI_RUNNING',
  ReadyToMerge: 'READY_TO_MERGE',
  YourDrafts: 'YOUR_DRAFTS',
  WaitingForReview: 'WAITING_FOR_REVIEW',
  AwaitingReview: 'AWAITING_REVIEW',
  MergeQueue: 'MERGE_QUEUE',
} as const

const DeferredCategorySchema = Type.Union([
  Type.Literal(DeferredCategory.MergeConflicts),
  Type.Literal(DeferredCategory.OutdatedBranch),
  Type.Literal(DeferredCategory.ChangesRequested),
  Type.Literal(DeferredCategory.CiFailing),
  Type.Literal(DeferredCategory.CiRunning),
  Type.Literal(DeferredCategory.ReadyToMerge),
  Type.Literal(DeferredCategory.YourDrafts),
  Type.Literal(DeferredCategory.WaitingForReview),
  Type.Literal(DeferredCategory.AwaitingReview),
  Type.Literal(DeferredCategory.MergeQueue),
])

export type DeferredCategory = Type.Static<typeof DeferredCategorySchema>

const LabelSchema = Type.Object({
  color: Type.Optional(Type.String()),
  description: Type.Optional(Type.String()),
  id: Type.Number(),
  name: Type.String(),
  nameHtml: Type.Optional(Type.String()),
})
export type Label = Type.Static<typeof LabelSchema>

const PullItemAssigneeSchema = Type.Object({
  login: Type.String(),
  avatarUrl: Type.String(),
  isCopilot: Type.Optional(Type.Boolean()),
  /** The value for an `assignee:` search qualifier, e.g. `@copilot`, `@claude[agent]`, or `app/<slug>` for bots. */
  filterValue: Type.Optional(Type.String()),
})
export type PullItemAssignee = Type.Static<typeof PullItemAssigneeSchema>

export const PullItemSchema = Type.Object({
  itemType: Type.Optional(Type.Literal('pull_request')),
  agentAttributedUser: Type.Optional(Type.Union([Type.Object({displayLogin: Type.String()}), Type.Null()])),
  assignees: Type.Optional(Type.Array(PullItemAssigneeSchema)),
  author: Type.Object({
    displayLogin: Type.String(),
    displayName: Type.Optional(Type.String()),
    isAgent: Type.Optional(Type.Boolean()),
    identity: Type.Optional(Type.Enum(['BOT', 'AI', 'MANNEQUIN'])),
    association: Type.Optional(
      Type.Enum([
        'COLLABORATOR',
        'CONTRIBUTOR',
        'FIRST_TIMER',
        'FIRST_TIME_CONTRIBUTOR',
        'MANNEQUIN',
        'MEMBER',
        'NONE',
        'OWNER',
      ]),
    ),
  }),
  attributedUsername: Type.Optional(Type.String()),
  commentCount: Type.Number(),
  commitHeadShaChannel: Type.Optional(Type.String()),
  aliveChannel: Type.Optional(Type.String()),
  createdAt: Type.String(),
  updatedAt: Type.String(),
  closedAt: Type.Optional(Type.String()),
  mergedAt: Type.Optional(Type.String()),
  displayState: Type.Optional(DisplayStateSchema),
  headSha: Type.Optional(Type.String()),
  labels: Type.Array(LabelSchema),
  // Numeric database id, or (once backends emit it directly) the GraphQL global ID as a string.
  // Use `getListItemIdentity` (not this field directly) for render keys, cache keys, and the
  // deferred merge, since it also covers the legacy `globalId` and composite fallbacks below.
  id: Type.Union([Type.Number(), Type.String()]),
  // Legacy globally-unique, collision-free identity. Encodes the object type +
  // database id, so it never collides with an issue's `id` in the mixed list.
  // Backends are migrating to emit this value directly as `id` (a string) and will stop sending
  // this field. Kept optional as a safety net for a transient backend deploy skew where a
  // payload might omit `globalId`; consumers should fall back to the composite
  // `${itemType}-${id}` key when it's missing. See `getListItemIdentity`.
  globalId: Type.Optional(Type.String()),
  isDraft: Type.Boolean(),
  linkedIssueCount: Type.Optional(Type.Number()),
  linkedIssueUrl: Type.Optional(Type.String()),
  mergeQueueUrl: Type.Optional(Type.String()),
  milestone: Type.Optional(Type.Object({title: Type.String(), url: Type.String()})),
  number: Type.Number(),
  permalink: Type.String(),
  isReadByCurrentUser: Type.Boolean(),
  repoNameWithOwner: Type.String(),
  category: Type.Optional(DeferredCategorySchema),
  stackPosition: Type.Union([Type.Number(), Type.Null()]),
  stackSize: Type.Union([Type.Number(), Type.Null()]),
  state: StateSchema,
  taskListItemCount: Type.Optional(Type.Number()),
  completeTaskListItemCount: Type.Optional(Type.Number()),
  title: Type.String(),
  titleHtml: Type.Optional(SafeHTML()),
})
export type PullItem = Type.Static<typeof PullItemSchema>

export type PullListItem = Omit<PullItem, 'id' | 'labels' | 'titleHtml'> & {
  id: number | bigint | string
  labels: Array<
    Omit<Label, 'id' | 'nameHtml'> & {id: number | bigint | string; nameHtml?: Label['nameHtml'] | SafeHtml}
  >
  titleHtml?: PullItem['titleHtml'] | SafeHtml
}

export type ListItem = PullItem | IssueItem

export const ReviewDecisionState = {
  Approved: 'APPROVED',
  ChangesRequested: 'CHANGES_REQUESTED',
  ReviewRequired: 'REVIEW_REQUIRED',
} as const

export type ReviewDecisionState = (typeof ReviewDecisionState)[keyof typeof ReviewDecisionState]

export const StatusCheckState = {
  Success: 'SUCCESS',
  Failure: 'FAILURE',
  Pending: 'PENDING',
} as const

export type StatusCheckState = (typeof StatusCheckState)[keyof typeof StatusCheckState]

const StatusCheckStateSchema = Type.Enum(Object.values(StatusCheckState))

const StatusCheckRollupSchema = Type.Object({
  state: StatusCheckStateSchema,
  successCount: Type.Number(),
  totalCount: Type.Number(),
})

export type StatusCheckRollup = Type.Static<typeof StatusCheckRollupSchema>

const ReactionGroupSchema = Type.Object({
  content: Type.String(),
  totalCount: Type.Number(),
})

export const DeferredItemSchema = Type.Object({
  itemType: Type.Optional(Type.Literal('pull_request')),
  id: Type.Union([Type.Number(), Type.String()]),
  globalId: Type.Optional(Type.String()),
  reviewDecisionState: Type.Optional(Type.Enum(Object.values(ReviewDecisionState))),
  statusCheckRollup: Type.Optional(StatusCheckRollupSchema),
  approvedReviewsCount: Type.Optional(Type.Number()),
  changesRequestedReviewsCount: Type.Optional(Type.Number()),
  reactionGroups: Type.Optional(Type.Union([Type.Array(ReactionGroupSchema), Type.Null()])),
})

export type DeferredItem = Type.Static<typeof DeferredItemSchema>

export type ListDeferredItem = DeferredItem | IssueDeferredItem

type PullEnrichment = Partial<
  Omit<DeferredItem, 'id' | 'globalId'> & {
    deferredDataStatus: 'pending' | 'error' | 'success'
  }
>

export type EnrichedPull = PullItem & PullEnrichment
export type EnrichedPullListItem = PullListItem & PullEnrichment

export interface SectionQuery {
  /** The raw search query string, e.g. `is:open is:pr review-requested:@me`. */
  query: string
  /** Optional sub-label for composite sections, e.g. "Failing checks". */
  label?: string
}

export interface DashboardSectionConfig {
  id: string
  name: string
  maxItems: number
  href: string
  emptyDescription?: string
  /** Short, user-facing description shown in the Customize sections dialog. */
  description?: string
  /** The search query/queries that determine which PRs appear in this section. */
  queries?: SectionQuery[]
}

export interface CategoryDisplay {
  label: string
  variant: LabelProps['variant']
  icon?: Icon
  iconColor?: string
  iconColorClass?: string
}
